import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { ManageHeader, SearchBar, Fab, EmptyState, Field, TextInput, TextArea, Toggle } from '../../components/mobile/ManageKit';
import { Trash2, Loader2 } from 'lucide-react';

const empty = { name: '', description: '', default_rate: '', color: '#8B5CF6', is_active: true };

const MobileManageWorkerTypes = () => {
  const { api } = useMobileAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/worker-types/all');
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load worker types');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setConfirmDel(false); setOpen(true); };
  const openEdit = (w) => { setEditing(w); setForm({ ...empty, ...Object.fromEntries(Object.entries(w).filter(([, v]) => v != null)), default_rate: w.default_rate ?? '' }); setConfirmDel(false); setOpen(true); };

  const save = async () => {
    if (!(form.name || '').trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        default_rate: form.default_rate === '' ? null : Number(form.default_rate),
        color: form.color || '#8B5CF6',
        is_active: form.is_active,
      };
      if (editing) await api.put(`/api/worker-types/${editing.id}`, payload);
      else await api.post('/api/worker-types', payload);
      toast.success(editing ? 'Worker type updated' : 'Worker type added');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/api/worker-types/${editing.id}`);
      toast.success('Worker type deleted');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot delete');
    }
  };

  const filtered = items.filter((w) => w.name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <MobileLayout>
      <ManageHeader title="Worker Types" subtitle={`${items.length} total`} />
      <div className="p-4 space-y-3">
        <SearchBar value={q} onChange={setQ} placeholder="Search worker types..." />
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 text-[#ffd108] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No worker types" subtitle="Tap Add to create one" />
        ) : (
          <div className="space-y-2" data-testid="worker-types-list">
            {filtered.map((w) => (
              <button key={w.id} onClick={() => openEdit(w)}
                className="w-full flex items-center gap-3 bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-lg px-4 py-3 text-left active:bg-[var(--m-bg-elevated)]"
                data-testid={`worker-type-row-${w.id}`}>
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: w.color || '#8B5CF6' }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--m-text)] font-medium truncate flex items-center gap-2">
                    {w.name}
                    {!w.is_active && <span className="text-[10px] text-[var(--m-text-muted)] border border-[var(--m-border-light)] px-1.5 rounded">Inactive</span>}
                  </div>
                  {w.description && <div className="text-xs text-[var(--m-text-secondary)] truncate">{w.description}</div>}
                </div>
                {w.is_system && <span className="text-[10px] font-bold uppercase text-[var(--m-text-secondary)] bg-[var(--m-bg-elevated)]/40 px-2 py-0.5 rounded-full">System</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <Fab onClick={openNew} label="Add" testId="worker-type-add-fab" />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[var(--m-bg)] border-[var(--m-border)] rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader><SheetTitle className="text-[var(--m-text)] text-left">{editing ? 'Edit Worker Type' : 'New Worker Type'}</SheetTitle></SheetHeader>
          <div className="py-4 space-y-3">
            <Field label="Name" required><TextInput testId="wt-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dogman" /></Field>
            <Field label="Description"><TextArea testId="wt-desc" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <Field label="Default hourly rate" hint="Optional"><TextInput testId="wt-rate" type="number" inputMode="decimal" value={form.default_rate} onChange={(e) => setForm({ ...form, default_rate: e.target.value })} placeholder="0.00" /></Field>
            <Field label="Colour">
              <input type="color" value={form.color || '#8B5CF6'} onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="w-full h-11 bg-[var(--m-bg-input)] border border-[var(--m-border)] rounded-lg p-1" data-testid="wt-color" />
            </Field>
            <Toggle label="Active" checked={form.is_active} onChange={(v) => setForm({ ...form, is_active: v })} testId="wt-active" />
          </div>
          <div className="space-y-2 pb-2">
            <Button className="btn-volt w-full" onClick={save} disabled={saving} data-testid="wt-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save changes' : 'Add worker type')}
            </Button>
            {editing && !editing.is_system && (
              confirmDel ? (
                <Button variant="destructive" className="w-full" onClick={remove} data-testid="wt-delete-confirm">Tap again to confirm delete</Button>
              ) : (
                <Button variant="outline" className="w-full border-red-800 text-red-400" onClick={() => setConfirmDel(true)} data-testid="wt-delete">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              )
            )}
          </div>
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
};

export default MobileManageWorkerTypes;
